import React, {useContext, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import Scripts from "../../Common/Scripts/Scripts";
import CandidateSidebar from "../Dashboard/CandidateSidebar";
import CareHomeSidebar from "../Dashboard/CareHomeSideBar";
import Footer from "../Dashboard/Footer";
import Header from "../Dashboard/Header";
import {getSession} from "../../../utils/session";
import {EMPLOYR_LOGIN_SESSION} from "../../../constant/session";
import {ROLE} from "../../../constant/role";
import {AuthContext} from "../../../context/auth";
import {del, get, put} from "../../../api";
import Error from "../../Common/Alert/Error";
import Success from "../../Common/Alert/Success";
import {formatDateString} from "../../../utils/date";
import Admin from "../Dashboard/Admin";
import Modal from "../../Common/Modal/Modal";

const Messages = () => {
  const user = getSession(EMPLOYR_LOGIN_SESSION);
  const [isLoading, setLoading] = useState(true);
  const [inboxMessages, setInboxMessages] = useState([]);
  const [myActionMessages, setMyActionMessages] = useState([]);
  const [sentMessages, setSentMessages] = useState([]);
  const [subscriptionNotifications, setSubscriptionNotifications] = useState([]);
  const [reviewInboxItems, setReviewInboxItems] = useState([]);

  // removed unused userDetail state
  const [inboxApiStatus, setInboxApiStatus] = useState("");
  const [sentApiStatus, setSentApiStatus] = useState("");
  const [myActionApiStatus, setMyActionApiStatus] = useState("");
  const [subscriptionApiStatus, setSubscriptionApiStatus] = useState("");

  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const navigate = useNavigate();
  const { setUnreadMessages } = useContext(AuthContext);

  // removed unused callMethod function

  const getSubscriptionNotifications = async () => {
    setLoading(true);
    const url = `/message/messages?message-section=SENT`;
    try {
      const response = await get(url);
      const subscriptions = (response || []).filter(
        (m) => m?.role === 'Admin' && isSubscriptionMsg(m)
      );

      // Cache to avoid duplicate calls for same toId
      // Stores { name, startDate, endDate } keyed by toId
      const detailCache = new Map();

      const enriched = await Promise.all(
        subscriptions.map(async (m) => {
          let name = (m?.companyName || '').trim();
          let startDate = '';
          let endDate = '';

          if (!name) {
            try {
              const toId = m?.toId;
              if (toId) {
                if (detailCache.has(toId)) {
                  const cached = detailCache.get(toId);
                  name = cached?.name || name;
                  startDate = cached?.startDate || startDate;
                  endDate = cached?.endDate || endDate;
                } else {
                  const detail = await get(
                    `/user/users/${toId}/detail`
                  );
                  const derivedName =
                    detail?.userData?.company?.companyName ||
                    (Array.isArray(detail?.userData?.company?.sites) &&
                      detail?.userData?.company?.sites?.[0]?.name) ||
                    '';

                  // Extract subscription start/end dates, prefer active/current subscription
                  const subs = Array.isArray(detail?.userData?.company?.subscription)
                    ? detail.userData.company.subscription
                    : [];
                  const activeSub = subs.find(s => (s?.status || '').toLowerCase() === 'active') || subs[0];
                  const derivedStart = activeSub?.startDate || activeSub?.subscriptionDate || '';
                  const derivedEnd = activeSub?.endDate || '';

                  detailCache.set(toId, {
                    name: derivedName || name || `Company #${m?.companyId ?? ''}`,
                    startDate: derivedStart,
                    endDate: derivedEnd,
                  });

                  name = derivedName || name;
                  startDate = derivedStart || startDate;
                  endDate = derivedEnd || endDate;
                }
              }
            } catch (_) {
              // ignore and fallback below
            }
          }

          return {
            companyName: name || `Company #${m?.companyId ?? ''}`,
            plan: extractPlan(m?.message || ''),
            subscriptionDateTime: m?.createdAt || m?.entryDate,
            startDate,
            endDate,
            raw: m,
          };
        })
      );

      const mapped = enriched.sort(
        (a, b) => new Date(b?.subscriptionDateTime) - new Date(a?.subscriptionDateTime)
      );

      setSubscriptionNotifications(mapped);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      setSubscriptionApiStatus("error");
      console.log("error response", e);
    }
  };

  // helpers for subscriptions tab
  const isSubscriptionMsg = (msg) => {
    if (!msg?.message) return false;
    const text = String(msg.message).toLowerCase();
    return text.includes('subscription request') && text.includes('made active');
  };

  const extractPlan = (msgText) => {
    try {
      // Example: "Your subscription request from Free to Premium has been made Active."
      const match = msgText.match(/to\s+([A-Za-z]+)/i);
      return match?.[1] || '';
    } catch (_) {
      return '';
    }
  };

  const activateTab = (tabSelector) => {
    try {
      const container = document.querySelector('.default-tabs.style-two.tabs-box');
      if (!container) return;
      const tabButtons = container.querySelectorAll('.tab-buttons .tab-btn');
      const tabs = container.querySelectorAll('.tabs-content .tab');

      tabButtons.forEach((btn) => btn.classList.remove('active-btn'));
      tabs.forEach((tab) => (tab.style.display = 'none'));

      const target = container.querySelector(tabSelector);
      const targetBtn = Array.from(tabButtons).find((btn) => btn.getAttribute('data-tab') === tabSelector);
      if (target) target.style.display = 'block';
      if (targetBtn) targetBtn.classList.add('active-btn');
    } catch (e) {
      // fail-safe: ignore
    }
  };

  const handleOpenSubscriptions = (e) => {
    e?.preventDefault?.();
    if (user?.role === ROLE.ADMIN) {
      getSubscriptionNotifications();
    }
    activateTab('#tab4');
  };

  useEffect(() => {
    const session = user;
    getInBoxMessages();
    getSentMessages();
    getMyActionMessages();
    // Only admins see subscription notifications
    if (session?.role === ROLE.ADMIN) {
      getSubscriptionNotifications();
      getReviewSubscriptionsForInbox();
    }
  }, []);

  // Fetch subscription review requests for admin inbox view
  const getReviewSubscriptionsForInbox = async () => {
    try {
      const url = `/user/company/subscription?type=Review`;
      const response = await get(url);
      const list = Array.isArray(response) ? response : [];
      const mapped = list.map((r) => ({
        // Synthetic message shape to fit inbox table
        messageId: `sub-${r?.subscriptionId}`,
        message: `${r?.companyName || 'A company'} has requested for subscription of ${r?.toSubscription || ''}`.trim(),
        fromFullName: r?.companyName || 'Company',
        entryDate: r?.subscriptionDate || r?.startDate || new Date().toISOString(),
        type: 'Subscription',
        isSynthetic: true,
        _source: r,
      }));
      // Sort newest first similar to inbox
      mapped.sort((a, b) => new Date(b.entryDate) - new Date(a.entryDate));
      setReviewInboxItems(mapped);
    } catch (e) {
      // fail silently for inbox augmentation
      console.log('Review subscriptions fetch failed', e);
    }
  };

  const handleViewMessage = (message) => {
    console.log('Processing message:', message);
    if (!message?.isSynthetic && message?.messageId) {
      unreadMessage(message.messageId);
    }

    // Check if it's a meeting-related message
    const isMeetingMessage = message.link &&
        (message.link.includes('/meetings') ||
            message.message.toLowerCase().includes('schedule') ||
            message.message.toLowerCase().includes('reschedule'));

    // Check if it's a job application message (for care home users)
    const isJobApplicationMessage = user?.role === ROLE.CARE_HOME &&
        (message.message.toLowerCase().includes('applied') ||
            message.message.toLowerCase().includes('application') ||
            message.message.toLowerCase().includes('applied for job'));

    // Check if it's a rejection message (for any role)
    const lowerCaseMessage = message.message.toLowerCase();
    const isRejectionMessage = lowerCaseMessage.includes('we are sorry') ||
        lowerCaseMessage.includes('unsuccessful') ||
        lowerCaseMessage.includes('Meeting has been declined for job') ||
        lowerCaseMessage.includes('please keep trying');

    console.log('Is meeting message?', isMeetingMessage);
    console.log('Is job application message?', isJobApplicationMessage);
    console.log('Is rejection message?', isRejectionMessage);

    if (isRejectionMessage) {
      // Show modal for rejection messages (highest priority)
      setSelectedMessage(message);
      setShowMessageModal(true);
    }
    else if (isMeetingMessage) {
      const urlParams = new URLSearchParams(message.link.split('?')[1]);
      const meetingId = urlParams.get('meetingId') || '';
      console.log('Extracted meetingId:', meetingId);
      navigate(`/meetings${meetingId ? `?id=${meetingId}` : ''}`);
    }
    else if (isJobApplicationMessage) {
      // Redirect to applicants page for care home users
      navigate('/applicants');
    }
    else {
      // Default behavior - show modal
      setSelectedMessage(message);
      setShowMessageModal(true);
    }
  };

  const getInBoxMessages = async () => {
    setLoading(true);
    const url = `/message/messages?message-section=INBOX`;
    try {
      const response = await get(url);
      if(response?.length > 0){
        setInboxMessages(response.sort((a,b) => new Date(b?.entryDate) - new Date(a?.entryDate)));
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);
      console.log("error response", e);
    }
  };

  const getSentMessages = async () => {
    setLoading(true);
    const url = `/message/messages?message-section=SENT`;
    try {
      const response = await get(url);
      if(response?.length > 0){
        setSentMessages(response.sort((a,b) => new Date(b?.entryDate) - new Date(a?.entryDate)));
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);
      console.log("error response", e);
    }
  };

  const getMyActionMessages = async () => {
    setLoading(true);
    const url = `/message/messages?message-section=ACTION_ITEMS`;
    try {
      const response = await get(url);
      if(response?.length > 0){
        setMyActionMessages(response.sort((a,b) => new Date(b?.entryDate) - new Date(a?.entryDate)));
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);
      console.log("error response", e);
    }
  };

  const deleteMessage = async (id, messageType) => {
    setLoading(true);
    const url = `/message/messages?messageId=${id}`;
    setInboxApiStatus("");
    setSentApiStatus("");
    setMyActionApiStatus("");
    try {
      const response = await del(url);
      if (response?.status === 200 || response?.status === 204) {
        if (messageType === "inbox") {
          getInBoxMessages();
          setInboxApiStatus("success");
        } else if (messageType === "sent") {
          getSentMessages();
          setSentApiStatus("success");
        } else if (messageType === "myAction") {
          getMyActionMessages();
          setMyActionApiStatus("success");
        }
        getStats();
      } else {
        if (messageType === "inbox") {
          setInboxApiStatus("error");
        } else if (messageType === "sent") {
          setSentApiStatus("error");
        } else if (messageType === "myAction") {
          setMyActionApiStatus("error");
        }
        setLoading(false);
      }
    } catch (e) {
      setLoading(false);
      if (messageType === "inbox") {
        setInboxApiStatus("error");
      } else if (messageType === "sent") {
        setSentApiStatus("error");
      } else if (messageType === "myAction") {
        setMyActionApiStatus("error");
      }
      console.log("error response", e);
    }
  };

  const unreadMessage = async (messagedId) => {
    try {
      const url = `/message/messages/read?isRead=true&messageIds=${messagedId}`;
      const response = await put(url);
      console.log(response?.status);
      getStats();
    } catch (e) {
      console.log(e);
    }
  };

  const getStats = async () => {
    const url = `/user/stats/count`;
    try {
      const response = await get(url);
      setUnreadMessages(response?.unreadMsg);
    } catch (e) {
      // console.error(e);
    }
  };

  const getReadClassForCandidate = (itm) => {
    let output = "messageRead";
    if (itm?.candidateIsRead === true) {
      output = "";
    }
    return output;
  };

  const getReadClassForCareHome = (itm) => {
    let output = "messageRead";
    if (itm?.isRead === true) {
      output = "";
    }
    return output;
  };

  return (
      <>
        <div className="page-wrapper dashboard">
          <Scripts />

          {isLoading && <div className="preloader"></div>}
          {user?.role === ROLE.CANDIDATE && (
              <>
                <Header />
                <CandidateSidebar />
              </>
          )}
          {user?.role === ROLE.CARE_HOME && (
              <>
                <Header />
                <CareHomeSidebar />
              </>
          )}
          {user?.role === ROLE.ADMIN && <Admin />}

          {/* Message Detail Modal */}
          <Modal
              show={showMessageModal}
              onClose={() => setShowMessageModal(false)}
              title={`Message from ${selectedMessage?.fromFullName || selectedMessage?.companyName || 'Sender'}`}
          >
            {selectedMessage && (
                <div className="message-detail">
                  <div className="message-meta">
                    <div className="meta-item">
                      <span className="meta-label">Date:</span>
                      <span className="meta-value">{formatDateString(selectedMessage?.entryDate)}</span>
                    </div>
                    {selectedMessage?.type && (
                        <div className="meta-item">
                          <span className="meta-label">Type:</span>
                          <span className="meta-value">{selectedMessage.type}</span>
                        </div>
                    )}
                  </div>
                  <div className="message-content">
                    <p>{selectedMessage.message}</p>
                  </div>
                </div>
            )}
          </Modal>

          <section className="user-dashboard">
            <div className="dashboard-outer">
              <div className="upper-title-box">
                <h3>Message Centre</h3>
              </div>

              <div className="row">
                <div className="col-lg-12">
                  {/* <!-- Ls widget --> */}
                  <div className="ls-widget">
                    <div className="tabs-box">
                      <div className="widget-title"></div>

                      <div className="widget-content">
                        <div className="default-tabs style-two tabs-box">
                          {/* <!--Tabs Box--> */}
                          <ul className="tab-buttons clearfix">
                            <li className="tab-btn active-btn" data-tab="#tab1">
                              <span className="la la-inbox"></span> Inbox
                            </li>
                            <li className="tab-btn" data-tab="#tab2">
                              <span className="la la-share"></span> Sent
                            </li>
                            <li className="tab-btn" data-tab="#tab3">
                              <span className="la la-tasks"></span> My Action List
                            </li>
                          <li
                            className="tab-btn"
                            data-tab="#tab4"
                            style={{ display: user?.role === ROLE.ADMIN ? 'list-item' : 'none' }}
                            onClick={handleOpenSubscriptions}
                          >
                            <span className="la la-bell"></span> Subscriptions
                          </li>
                          </ul>

                          <div className="tabs-content">
                            {/* <!--Inbox--> */}
                            <div
                                className="tab active-tab animated fadeIn"
                                id="tab1"
                                style={{ display: "block" }}
                            >
                              <div className="row">
                                <div className="col-lg-12">
                                  <div className="notification-widget ls-widget">
                                    <div className="tabs-box">
                                      <div className="widget-title">
                                        <b>
                                          <span className="la la-inbox"></span>{" "}
                                          Received Messages
                                        </b>
                                      </div>
                                      {inboxApiStatus === "success" && (
                                          <>
                                            <Success msg="Your message has been successfully deleted.." />
                                          </>
                                      )}
                                      {inboxApiStatus === "error" && (
                                          <>
                                            <Error msg="Something went wrong while deleting message. Please try again" />
                                          </>
                                      )}

                                      <div className="widget-content">
                                        <div className="table-outer">
                                          <table className="default-table">
                                            <thead>
                                            <tr>
                                              <th>Action</th>
                                              <th>Message</th>
                                              <th>From</th>
                                              <th>Date</th>
                                              <th>For</th>
                                            </tr>
                                            </thead>

                                            <tbody>
                                            {(() => {
                                              const displayInboxItems = user?.role === ROLE.ADMIN
                                                ? [...reviewInboxItems, ...inboxMessages].sort((a,b) => new Date(b?.entryDate) - new Date(a?.entryDate))
                                                : inboxMessages;
                                              if (!displayInboxItems || displayInboxItems.length === 0) {
                                                return (
                                                  <tr>
                                                    <td colSpan="5">No message available</td>
                                                  </tr>
                                                );
                                              }
                                              return displayInboxItems.map((item) => (
                                                <tr
                                                  key={item?.messageId}
                                                  className={
                                                    user?.role === ROLE.CANDIDATE
                                                      ? getReadClassForCandidate(item)
                                                      : getReadClassForCareHome(item)
                                                  }
                                                >
                                                  <td style={{ paddingLeft: "0" }}>
                                                    <div className="option-box">
                                                      <ul className="option-list">
                                                        <li>
                                                          <button
                                                            data-text="View"
                                                            onClick={() => handleViewMessage(item)}
                                                            className="btn-view"
                                                          >
                                                            <span className="la la-eye"></span>
                                                          </button>
                                                        </li>
                                                        {!item?.isSynthetic && (
                                                          <li>
                                                            <button
                                                              data-text="Delete"
                                                              className="text-danger btn-delete"
                                                              onClick={(e) => {
                                                                e?.preventDefault();
                                                                e?.stopPropagation();
                                                                deleteMessage(item?.messageId, "inbox");
                                                              }}
                                                            >
                                                              <span className="la la-trash"></span>
                                                            </button>
                                                          </li>
                                                        )}
                                                      </ul>
                                                    </div>
                                                  </td>
                                                  <td>{item?.message}</td>
                                                  <td>
                                                    {user?.role === ROLE.CANDIDATE
                                                      ? item?.companyName
                                                      : item?.fromFullName}
                                                  </td>
                                                  <td>{formatDateString(item?.entryDate)}</td>
                                                  <td>{item?.type}</td>
                                                </tr>
                                              ));
                                            })()}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* <!--Sent--> */}
                            <div
                                className="tab"
                                id="tab2"
                                style={{ display: "none" }}
                            >
                              <div className="row">
                                <div className="col-lg-12">
                                  <div className="notification-widget ls-widget">
                                    <div className="tabs-box">
                                      <div className="widget-title">
                                        <b>
                                          <span className="la la-share"></span>{" "}
                                          Sent Messages
                                        </b>
                                      </div>
                                      {sentApiStatus === "success" && (
                                          <>
                                            <Success msg="Your message has been successfully deleted.." />
                                          </>
                                      )}
                                      {sentApiStatus === "error" && (
                                          <>
                                            <Error msg="Something went wrong while deleting message. Please try again" />
                                          </>
                                      )}

                                      <div className="widget-content">
                                        <div className="table-outer">
                                          <table className="default-table">
                                            <thead>
                                            <tr>
                                              <th>Action</th>
                                              <th>Message</th>
                                              <th>To</th>
                                              <th>Date</th>
                                            </tr>
                                            </thead>

                                            <tbody>
                                            {sentMessages?.length === 0 && (
                                                <tr>
                                                  <td colSpan="4">No message available</td>
                                                </tr>
                                            )}
                                            {sentMessages?.map((item) => {
                                              return (
                                                  <tr
                                                      key={item?.messageId}
                                                      className={
                                                        user?.role ===
                                                        ROLE.CANDIDATE
                                                            ? getReadClassForCandidate(
                                                                item
                                                            )
                                                            : getReadClassForCareHome(
                                                                item
                                                            )
                                                      }
                                                  >
                                                    <td
                                                        style={{
                                                          paddingLeft: "0",
                                                        }}
                                                    >
                                                      <div className="option-box">
                                                        <ul className="option-list">
                                                          <li>
                                                            <button
                                                                data-text="View"
                                                                onClick={() => handleViewMessage(item)}
                                                                className="btn-view"
                                                            >
                                                              <span className="la la-eye"></span>
                                                            </button>
                                                          </li>
                                                          <li>
                                                            <button
                                                                data-text="Delete"
                                                                className="text-danger btn-delete"
                                                                onClick={(e) => {
                                                                  e?.preventDefault();
                                                                  e?.stopPropagation();
                                                                  deleteMessage(
                                                                      item?.messageId,
                                                                      "sent"
                                                                  );
                                                                }}
                                                            >
                                                              <span className="la la-trash"></span>
                                                            </button>
                                                          </li>
                                                        </ul>
                                                      </div>
                                                    </td>
                                                    <td>{item?.message}</td>
                                                    <td>{item?.toFullName}</td>
                                                    <td>
                                                      {formatDateString(
                                                          item?.entryDate
                                                      )}
                                                    </td>
                                                  </tr>
                                              );
                                            })}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* <!--Action List--> */}
                            <div
                                className="tab"
                                id="tab3"
                                style={{ display: "none" }}
                            >
                              <div className="row">
                                <div className="col-lg-12">
                                  <div className="notification-widget ls-widget">
                                    <div className="tabs-box">
                                      <div className="widget-title">
                                        <b>
                                          <span className="la la-tasks"></span>{" "}
                                          For My Action
                                        </b>
                                      </div>
                                      {myActionApiStatus === "success" && (
                                          <>
                                            <Success msg="Your message has been successfully deleted.." />
                                          </>
                                      )}
                                      {myActionApiStatus === "error" && (
                                          <>
                                            <Error msg="Something went wrong while deleting message. Please try again" />
                                          </>
                                      )}

                                      <div className="widget-content">
                                        <div className="table-outer">
                                          <table className="default-table">
                                            <thead>
                                            <tr>
                                              <th>Action</th>
                                              <th>Message</th>
                                              <th>From</th>
                                              <th>Date</th>
                                              <th>For</th>
                                            </tr>
                                            </thead>

                                            <tbody>
                                            {myActionMessages?.length === 0 && (
                                                <tr>
                                                  <td colSpan="5">No message available</td>
                                                </tr>
                                            )}
                                            {myActionMessages?.map((item) => {
                                              return (
                                                  <tr
                                                      key={item?.messageId}
                                                      className={
                                                        user?.role ===
                                                        ROLE.CANDIDATE
                                                            ? getReadClassForCandidate(
                                                                item
                                                            )
                                                            : getReadClassForCareHome(
                                                                item
                                                            )
                                                      }
                                                  >
                                                    <td
                                                        style={{
                                                          paddingLeft: "0",
                                                        }}
                                                    >
                                                      <div className="option-box">
                                                        <ul className="option-list">
                                                          <li>
                                                            <button
                                                                data-text="View"
                                                                onClick={() => handleViewMessage(item)}
                                                                className="btn-view"
                                                            >
                                                              <span className="la la-eye"></span>
                                                            </button>
                                                          </li>
                                                          <li>
                                                            <button
                                                                data-text="Delete"
                                                                className="text-danger btn-delete"
                                                                onClick={(e) => {
                                                                  e?.preventDefault();
                                                                  e?.stopPropagation();
                                                                  deleteMessage(
                                                                      item?.messageId,
                                                                      "myAction"
                                                                  );
                                                                }}
                                                            >
                                                              <span className="la la-trash"></span>
                                                            </button>
                                                          </li>
                                                        </ul>
                                                      </div>
                                                    </td>
                                                    <td>{item?.message}</td>
                                                    <td>
                                                      {user?.role ===
                                                      ROLE.CANDIDATE
                                                          ? item?.companyName
                                                          : item?.fromFullName}
                                                    </td>
                                                    <td>
                                                      {formatDateString(
                                                          item?.entryDate
                                                      )}
                                                    </td>
                                                    <td>{item?.type}</td>
                                                  </tr>
                                              );
                                            })}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                          {/* <!--Subscription Notifications (Admin)--> */}
                          <div
                            className="tab"
                            id="tab4"
                            style={{ display: 'none' }}
                          >
                            <div className="row">
                              <div className="col-lg-12">
                                <div className="notification-widget ls-widget">
                                  <div className="tabs-box">
                                    <div className="widget-title">
                                      <b>
                                        <span className="la la-bell"></span>{" "}
                                        Subscription Notifications
                                      </b>
                                    </div>
                                    {subscriptionApiStatus === "error" && (
                                      <>
                                        <Error msg="Unable to load subscription notifications. Please try again" />
                                      </>
                                    )}

                                    <div className="widget-content">
                                      <div className="table-outer">
                                        <table className="default-table">
                                          <thead>
                                            <tr>
                                              <th>Business</th>
                                              <th>Plan</th>
                                              <th>Start Date</th>
                                              <th>End Date</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {user?.role === ROLE.ADMIN && subscriptionNotifications?.length === 0 && (
                                              <tr>
                                                <td colSpan="4">No subscription notifications</td>
                                              </tr>
                                            )}
                                            {user?.role === ROLE.ADMIN && subscriptionNotifications?.map((item, idx) => (
                                              <tr key={item?.id || item?.messageId || idx}>
                                                <td>{item?.companyName || item?.businessName || "N/A"}</td>
                                                <td>{item?.plan || item?.subscriptionType || item?.subscriptionPlan || "N/A"}</td>
                                                <td>{formatDateString(item?.startDate || item?.subscriptionDateTime || item?.entryDate)}</td>
                                                <td>{item?.endDate ? formatDateString(item.endDate) : 'N/A'}</td>
                                              </tr>
                                            ))}
                                            {user?.role !== ROLE.ADMIN && (
                                              <tr>
                                                <td colSpan="4">Unauthorized</td>
                                              </tr>
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <Footer />
        </div>
      </>
  );
};

export default Messages;